import React, { useState } from 'react'
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import { Input, Output, Variable } from '../types'

interface VariableManagerProps {
  variables: Variable[]
  outputs: Output[]
  inputs: Input[]
  onVariablesChange: (variables: Variable[]) => void
  onOutputChange: (variables: Output[]) => void
  onInputChange: (variables: Input[]) => void
  className?: string
}

const VariableManager: React.FC<VariableManagerProps> = ({
  variables,
  outputs,
  inputs,
  onVariablesChange,
  onOutputChange,
  onInputChange,
  className = ''
}) => {
  const [newVariableName, setNewVariableName] = useState('')
  const [newVariableType, setNewVariableType] = useState('Boolean')
  const [newVariableIsArray, setNewVariableIsArray] = useState(false)
  const [newOutputName, setNewOutputName] = useState('')
  const [newOutputType, setNewOutputType] = useState('Boolean')
  const [newOutputIsArray, setNewOutputIsArray] = useState(false)
  const [newInputName, setNewInputName] = useState('')
  const [newInputType, setNewInputType] = useState('Boolean')
  const [newInputIsArray, setNewInputIsArray] = useState(false)
  const [isExpandedVariables, setIsExpandedVariables] = useState(false)
  const [isExpandedOutput, setIsExpandedOutput] = useState(false)
  const [isExpandedInput, setIsExpandedInput] = useState(false)

  const addVariable = () => {
    if (newVariableName.trim()) {
      const newVariable: Variable = {
        id: `var_${Date.now()}`,
        name: newVariableName.trim(),
        typeName: newVariableType,
        isArray: newVariableIsArray,
        value: null,
        storageDriverTypeName: "Elsa.Workflows.WorkflowInstanceStorageDriver, Elsa.Workflows.Core"
      }
      onVariablesChange([...variables, newVariable])
      setNewVariableName('')
      setNewVariableType('Boolean')
      setNewVariableIsArray(false)
    }
  }

  const removeVariable = (index: number) => {
    onVariablesChange(variables.filter((_, i) => i !== index))
  }

  const updateVariable = (index: number, field: keyof Variable, value: any) => {
    const updatedVariables = [...variables]
    updatedVariables[index] = { ...updatedVariables[index], [field]: value }
    onVariablesChange(updatedVariables)
  }

  const addOutput = () => {
    if (newOutputName.trim()) {
      const newOutput: Output = {
        id: `output_${Date.now()}`,
        type: newOutputType,
        isArray: newOutputIsArray,
        name: newOutputName.trim(),
        displayName: newOutputName.trim(),
        description: '',
        category: 'Primitives',
      }
      onOutputChange([...outputs, newOutput])
      setNewOutputName('')
      setNewOutputType('Boolean')
      setNewOutputIsArray(false)
    }
  }

  const removeOutput = (index: number) => {
    onOutputChange(outputs.filter((_, i) => i !== index))
  }

  const updateOutput = (index: number, field: keyof Output, value: any) => {
    const updateOutputs = [...outputs]
    updateOutputs[index] = { ...updateOutputs[index], [field]: value }
    onOutputChange(updateOutputs)
  }

  const addInput = () => {
    if (newInputName.trim()) {
      const newInput: Input = {
        id: `input_${Date.now()}`,
        uiHint: 'singleline',
        storageDriverType: 'Elsa.Workflows.WorkflowInstanceStorageDriver, Elsa.Workflows.Core',
        type: newInputType,
        isArray: newInputIsArray,
        name: newInputName.trim(),
        displayName: newInputName.trim(),
        description: '',
        category: 'Primitives',
      }
      onInputChange([...inputs, newInput])
      setNewInputName('')
      setNewInputType('Boolean')
      setNewInputIsArray(false)
    }
  }

  const removeInput = (index: number) => {
    onInputChange(inputs.filter((_, i) => i !== index))
  }

  const updateInput = (index: number, field: keyof Input, value: any) => {
    const updatedInputs = [...inputs]
    updatedInputs[index] = { ...updatedInputs[index], [field]: value }
    onInputChange(updatedInputs)
  }

  return (
    <div className={`space-y-4 ${className}`}>

      {/* Variable properties */}
      <div>
        <div className="flex items-center justify-between">
          <h4 className="text-base font-bold text-slate-700 dark:text-zink-200">Variables</h4>
          <button
            onClick={() => setIsExpandedVariables(!isExpandedVariables)}
            className="p-1 hover:bg-slate-100 dark:hover:bg-zink-600 rounded transition-colors"
          >
            {isExpandedVariables ? (
              <ChevronUp className="h-4 w-4 text-slate-500 dark:text-zink-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-slate-500 dark:text-zink-400" />
            )}
          </button>
        </div>

        {isExpandedVariables && (
          <>
            {/* Add New Variable */}
            <div className="border border-slate-200 dark:border-zink-600 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                    value={newVariableName}
                    onChange={(e) => setNewVariableName(e.target.value)}
                    placeholder="e.g., IsReply"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        addVariable()
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                    Type
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                    value={newVariableType}
                    onChange={(e) => setNewVariableType(e.target.value)}
                  >
                    <option value="Boolean">Boolean</option>
                    <option value="String">String</option>
                    <option value="Number">Number</option>
                    <option value="DateTime">DateTime</option>
                    <option value="Object">Object</option>
                    <option value="ObjectDictionary">ObjectDictionary</option>
                  </select>
                </div>
                <div>
                  <button
                    onClick={addVariable}
                    disabled={!newVariableName.trim()}
                    className="w-full px-2 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Variables List */}
            <div className="space-y-2 mt-1">
              {variables.length === 0 ? (
                <div className="text-center py-1 text-slate-500 dark:text-zink-400 border-2 border-dashed border-slate-300 dark:border-zink-600 rounded-lg">
                  No variables defined
                </div>
              ) : (
                variables.map((variable, index) => (
                  <div
                    key={variable.id}
                    className="flex items-center justify-between p-3 bg-slate-50 dark:bg-zink-600 rounded-lg border border-slate-200 dark:border-zink-500"
                  >
                    <div className="flex items-center gap-4">
                      <input
                        title={variable.name}
                        type="text"
                        className="w-10 font-medium text-slate-700 dark:text-zink-200 bg-transparent border-none p-0"
                        value={variable.name}
                        onChange={(e) => updateVariable(index, 'name', e.target.value)}
                        onBlur={(e) => {
                          if (!e.target.value.trim()) {
                            // Reset to original name if empty
                            updateVariable(index, 'name', variable.name)
                          }
                        }}
                      />

                      <select
                        className="ml-2 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded border-none"
                        value={variable.typeName}
                        onChange={(e) => updateVariable(index, 'typeName', e.target.value)}
                      >
                        <option value="Boolean">Boolean</option>
                        <option value="String">String</option>
                        <option value="Number">Number</option>
                        <option value="DateTime">DateTime</option>
                        <option value="Object">Object</option>
                        <option value="ObjectDictionary">ObjectDictionary</option>
                      </select>
                    </div>
                    <div className="flex items-center">
                      <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-zink-300">
                        <input
                          type="checkbox"
                          checked={variable.isArray}
                          onChange={(e) => updateVariable(index, 'isArray', e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600"
                        />
                        Array
                      </label>
                    </div>
                    <div>
                      <button
                        onClick={() => removeVariable(index)}
                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>


      {/* Input properties */}
      <div>
        <div className="flex items-center justify-between">
          <h4 className="text-base font-bold text-slate-700 dark:text-zink-200">Input</h4>
          <button
            onClick={() => setIsExpandedInput(!isExpandedInput)}
            className="p-1 hover:bg-slate-100 dark:hover:bg-zink-600 rounded transition-colors"
          >
            {isExpandedInput ? (
              <ChevronUp className="h-4 w-4 text-slate-500 dark:text-zink-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-slate-500 dark:text-zink-400" />
            )}
          </button>
        </div>
        {isExpandedInput && (
          <>
            {/* Add New Input */}
            <div className="border border-slate-200 dark:border-zink-600 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                    value={newInputName}
                    onChange={(e) => setNewInputName(e.target.value)}
                    placeholder="e.g., IsReply"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        addInput()
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                    Type
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                    value={newInputType}
                    onChange={(e) => setNewInputType(e.target.value)}
                  >
                    <option value="Boolean">Boolean</option>
                    <option value="String">String</option>
                    <option value="Number">Number</option>
                    <option value="DateTime">DateTime</option>
                    <option value="Object">Object</option>
                    <option value="ObjectDictionary">ObjectDictionary</option>
                  </select>
                </div>
                <div>
                  <button
                    onClick={addInput}
                    disabled={!newInputName.trim()}
                    className="w-full px-2 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Inputs List */}
            <div className="space-y-2 mt-1">
              {inputs.length === 0 ? (
                <div className="text-center py-1 text-slate-500 dark:text-zink-400 border-2 border-dashed border-slate-300 dark:border-zink-600 rounded-lg">
                  No inputs defined.
                </div>
              ) : (
                inputs.map((input, index) => (
                  <div
                    key={input.id}
                    className="flex items-center justify-between p-3 bg-slate-50 dark:bg-zink-600 rounded-lg border border-slate-200 dark:border-zink-500"
                  >
                    <div className="flex items-center gap-4">
                      <input
                        title={input.name}
                        type="text"
                        className="w-10 font-medium text-slate-700 dark:text-zink-200 bg-transparent border-none p-0 focus:outline-none focus:ring-0"
                        value={input.name}
                        onChange={(e) => updateInput(index, 'name', e.target.value)}
                        onBlur={(e) => {
                          if (!e.target.value.trim()) {
                            // Reset to original name if empty
                            updateInput(index, 'name', input.name)
                          }
                        }}
                      />
                      <select
                        className="ml-2 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded border-none"
                        value={input.type}
                        onChange={(e) => updateInput(index, 'type', e.target.value)}
                      >
                        <option value="Boolean">Boolean</option>
                        <option value="String">String</option>
                        <option value="Number">Number</option>
                        <option value="DateTime">DateTime</option>
                        <option value="Object">Object</option>
                        <option value="ObjectDictionary">ObjectDictionary</option>
                      </select>
                    </div>
                    <div className="flex items-center">
                      <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-zink-300">
                        <input
                          type="checkbox"
                          checked={input.isArray}
                          onChange={(e) => updateInput(index, 'isArray', e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600"
                        />
                        Array
                      </label>
                    </div>
                    <button
                      onClick={() => removeInput(index)}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* Output properties */}
      <div>
        <div className="flex items-center justify-between">
          <h4 className="text-base font-bold text-slate-700 dark:text-zink-200">Output</h4>
          <button
            onClick={() => setIsExpandedOutput(!isExpandedOutput)}
            className="p-1 hover:bg-slate-100 dark:hover:bg-zink-600 rounded transition-colors"
          >
            {isExpandedOutput ? (
              <ChevronUp className="h-4 w-4 text-slate-500 dark:text-zink-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-slate-500 dark:text-zink-400" />
            )}
          </button>
        </div>
        {isExpandedOutput && (
          <>
            {/* Add New Output */}
            <div className="border border-slate-200 dark:border-zink-600 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                    value={newOutputName}
                    onChange={(e) => setNewOutputName(e.target.value)}
                    placeholder="e.g., IsReply"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        addOutput()
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                    Type
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                    value={newOutputType}
                    onChange={(e) => setNewOutputType(e.target.value)}
                  >
                    <option value="Boolean">Boolean</option>
                    <option value="String">String</option>
                    <option value="Number">Number</option>
                    <option value="DateTime">DateTime</option>
                    <option value="Object">Object</option>
                    <option value="ObjectDictionary">ObjectDictionary</option>
                  </select>
                </div>
                <div>
                  <button
                    onClick={addOutput}
                    disabled={!newOutputName.trim()}
                    className="w-full px-2 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Outputs List */}
            <div className="space-y-2 mt-1">
              {outputs.length === 0 ? (
                <div className="text-center py-1 text-slate-500 dark:text-zink-400 border-2 border-dashed border-slate-300 dark:border-zink-600 rounded-lg">
                  No outputs defined.
                </div>
              ) : (
                outputs.map((output, index) => (
                  <div
                    key={output.id}
                    className="flex items-center justify-between p-3 bg-slate-50 dark:bg-zink-600 rounded-lg border border-slate-200 dark:border-zink-500"
                  >
                    <div className="flex items-center gap-4">
                      <input
                        title={output.name}
                        type="text"
                        className="w-10 font-medium text-slate-700 dark:text-zink-200 bg-transparent border-none p-0 focus:outline-none focus:ring-0"
                        value={output.name}
                        onChange={(e) => updateOutput(index, 'name', e.target.value)}
                        onBlur={(e) => {
                          if (!e.target.value.trim()) {
                            // Reset to original name if empty
                            updateOutput(index, 'name', output.name)
                          }
                        }}
                      />
                      <select
                        className="ml-2 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded border-none"
                        value={output.type}
                        onChange={(e) => updateOutput(index, 'type', e.target.value)}
                      >
                        <option value="Boolean">Boolean</option>
                        <option value="String">String</option>
                        <option value="Number">Number</option>
                        <option value="DateTime">DateTime</option>
                        <option value="Object">Object</option>
                        <option value="ObjectDictionary">ObjectDictionary</option>
                      </select>
                    </div>
                    <div className="flex items-center">
                      <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-zink-300">
                        <input
                          type="checkbox"
                          checked={output.isArray}
                          onChange={(e) => updateOutput(index, 'isArray', e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600"
                        />
                        Array
                      </label>
                    </div>
                    <button
                      onClick={() => removeOutput(index)}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

    </div>
  )
}

export default VariableManager
